import { FC, useState } from 'react';
import { TableBody as Body, IconButton, Tooltip } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

import { Badge, TableNoData } from '../../shared/uiComponents';
import { HeaderProperties } from '../../shared/uiComponents/Table/tableProperties';
import { CustomTableRow, RowTableCell } from '../../shared/uiComponents/Table/tableHelpers';
import { InsuranceDetailProperties } from '../../redux/API/ClientAPIHelpers/insuranceProperties';
import InsuranceDetailModal from './InsuranceDetail';
import { Info } from '../../shared/Assets/svgIcons';
import { getBranchBadgeType } from '../../shared/Helpers/constants';
import { BadgeTypes } from '../../shared/uiComponents/StatusBadge';

interface RowRendererProperties {
	data: InsuranceDetailProperties[];
}

export const Headers: HeaderProperties[] = [
	{ id: '0', name: '' },
	{ id: '1', name: 'Name' },
	{ id: '2', name: 'Status' },
	{ id: '3', name: 'Type' },
	{ id: '4', name: 'Clients' },
	{ id: '5', name: 'Providers' },
	{ id: '6', name: 'Branch' },
	{ id: '7', name: '' },
];

const getInsuranceTypeLabel = (insurance: InsuranceDetailProperties): string => {
	if (insurance.parentId) return 'Sub-Insurance';
	if (insurance.isOutOfNetwork) return 'Out of Network';

	return 'In Network';
};

const getInsuranceTypeColor = (insurance: InsuranceDetailProperties): BadgeTypes => {
	if (insurance.parentId) return 'gray';
	if (insurance.isOutOfNetwork) return 'warning';

	return 'success';
};

const SubInsuranceAction: FC<{ insuranceId: string }> = ({ insuranceId }) => {
	const [modalOpen, setModalOpen] = useState(false);

	return (
		<>
			<Tooltip title="More information">
				<div onClick={() => setModalOpen(true)} style={{ cursor: 'pointer' }}>
					<Info />
				</div>
			</Tooltip>
			<InsuranceDetailModal insuranceId={insuranceId} open={modalOpen} onClose={() => setModalOpen(false)} />
		</>
	);
};

const InsuranceRow: FC<{
	insurance: InsuranceDetailProperties;
	subInsurances: InsuranceDetailProperties[];
}> = ({ insurance, subInsurances }) => {
	const [expanded, setExpanded] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);
	const hasSubInsurances = subInsurances.length > 0;

	return (
		<>
			<CustomTableRow>
				<RowTableCell style={{ width: '60px', padding: '8px' }}>
					{hasSubInsurances && (
						<IconButton size="small" onClick={() => setExpanded(!expanded)} style={{ padding: '4px' }}>
							{expanded ? <ExpandLess /> : <ExpandMore />}
						</IconButton>
					)}
				</RowTableCell>
				<RowTableCell>{insurance.name}</RowTableCell>
				<RowTableCell>
					<Badge
						label={insurance.isActive ? 'Active' : 'Inactive'}
						sizeType="sm"
						type={insurance.isActive ? 'solidSuccess' : 'solidGray'}
					/>
				</RowTableCell>
				<RowTableCell>
					<Badge label={getInsuranceTypeLabel(insurance)} sizeType="sm" type={getInsuranceTypeColor(insurance)} />
				</RowTableCell>
				<RowTableCell>{insurance.clientCount}</RowTableCell>
				<RowTableCell>{insurance.userCount}</RowTableCell>
				<RowTableCell>
					<div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
						{insurance.branches?.length ? (
							insurance.branches.map((branch) => (
								<Badge key={branch.id} label={branch.name} type={getBranchBadgeType(branch.state)} sizeType="sm" />
							))
						) : (
							<span style={{ color: '#999', fontSize: '12px' }}>-</span>
						)}
					</div>
				</RowTableCell>
				<RowTableCell>
					<Tooltip title="More information">
						<div onClick={() => setModalOpen(true)} style={{ cursor: 'pointer' }}>
							<Info />
						</div>
					</Tooltip>
				</RowTableCell>
			</CustomTableRow>

			<InsuranceDetailModal insuranceId={insurance.id} open={modalOpen} onClose={() => setModalOpen(false)} />

			{hasSubInsurances &&
				subInsurances.map((subInsurance) => (
					<CustomTableRow
						key={subInsurance.id}
						style={{
							backgroundColor: '#f9f9f9',
							display: expanded ? 'table-row' : 'none',
						}}
					>
						<RowTableCell style={{ width: '60px' }} />
						<RowTableCell>{subInsurance.name}</RowTableCell>
						<RowTableCell>
							<Badge
								label={subInsurance.isActive ? 'Active' : 'Inactive'}
								sizeType="sm"
								type={subInsurance.isActive ? 'success' : 'gray'}
							/>
						</RowTableCell>
						<RowTableCell>
							<Badge
								label={getInsuranceTypeLabel(subInsurance)}
								sizeType="sm"
								type={getInsuranceTypeColor(subInsurance)}
							/>
						</RowTableCell>
						<RowTableCell>{subInsurance.clientCount}</RowTableCell>
						<RowTableCell>{subInsurance.userCount}</RowTableCell>
						<RowTableCell>
							<div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
								{subInsurance.branches?.length ? (
									subInsurance.branches.map((branch) => (
										<Badge key={branch.id} label={branch.name} type={getBranchBadgeType(branch.state)} sizeType="sm" />
									))
								) : (
									<span style={{ color: '#999', fontSize: '12px' }}>-</span>
								)}
							</div>
						</RowTableCell>
						<RowTableCell>
							<SubInsuranceAction insuranceId={subInsurance.id} />
						</RowTableCell>
					</CustomTableRow>
				))}
		</>
	);
};

export const TableBody: FC<RowRendererProperties> = ({ data }) => {
	if (!data?.length) {
		return <TableNoData spanScope={Headers.length} />;
	}

	// Filter out only parent insurances (no parentId)
	const parentInsurances = data.filter((ins) => !ins.parentId);

	// Create a map of parent IDs to their sub-insurances
	const subInsuranceMap = new Map<string, InsuranceDetailProperties[]>();
	data.forEach((ins) => {
		if (ins.parentId) {
			if (!subInsuranceMap.has(ins.parentId)) {
				subInsuranceMap.set(ins.parentId, []);
			}
			subInsuranceMap.get(ins.parentId)?.push(ins);
		}
	});

	return (
		<Body>
			{parentInsurances.map((parent) => (
				<InsuranceRow key={parent.id} insurance={parent} subInsurances={subInsuranceMap.get(parent.id) || []} />
			))}
		</Body>
	);
};
